import React from 'react';

// Read-only "Under Contract" summary for a seller deal, so everything about the contract is on the
// deal page. Editing still happens in the Under Contract Form.

const money = (v) => {
  if (v === undefined || v === null || String(v).trim() === '') return '';
  const n = Number(String(v).replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? `$${n.toLocaleString('en-US')}` : String(v);
};
const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(`${d}T00:00:00`);
  return isNaN(dt) ? String(d) : dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
};
const join = (...p) => p.filter((x) => x && String(x).trim()).join(' ');
const todayIso = () => {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
};

// Whole days from today to an ISO date (negative = past).
export const daysBetween = (fromIso, toIso) => Math.round((Date.parse(`${toIso}T00:00:00Z`) - Date.parse(`${fromIso}T00:00:00Z`)) / 86400000);

// Deadlines on the contract, sorted by date, with days remaining. Pure so it can be tested.
export const buildUcDeadlines = (uc = {}, today = todayIso()) => {
  const mortgage = uc.financingType === 'Mortgage';
  const rows = [
    { key: 'earnestMoneyDueDate', label: 'Earnest money due', extra: money(uc.earnestMoneyAmount) },
    { key: 'secondEmdDue', label: '2nd EMD due', extra: money(uc.secondEmdAmount) },
    { key: 'dueDiligenceDeadline', label: 'Due diligence' },
    mortgage && { key: 'financingContingencyDeadline', label: 'Financing contingency' },
    uc.homeSaleContingency === 'Yes' && { key: 'homeSaleContingencyDate', label: 'Home sale contingency' },
    { key: 'walkThroughDate', label: 'Walk-through' },
    { key: 'forecastedClosedDate', label: 'Closing (forecast)' },
  ].filter(Boolean).filter((r) => uc[r.key] && /^\d{4}-\d{2}-\d{2}$/.test(uc[r.key]));
  const closed = !!uc.closedDate;
  return rows
    .map((r) => {
      const days = daysBetween(today, uc[r.key]);
      const state = closed ? 'done' : days < 0 ? 'past' : days <= 3 ? 'soon' : 'ok';
      return { ...r, date: uc[r.key], days, state };
    })
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
};

export const daysLabel = (d) => (d === 0 ? 'Today' : d === 1 ? 'Tomorrow' : d > 0 ? `${d} days left` : d === -1 ? '1 day past due' : `${-d} days past due`);

const STATE_STYLE = {
  past: { border: '#b00020', bg: '#FDECEE', pill: '#b00020', pillText: '#fff' },
  soon: { border: '#D4AF37', bg: '#FFF8E1', pill: '#D4AF37', pillText: '#1a1a1a' },
  ok: { border: '#e5dcc6', bg: '#ffffff', pill: '#1a1a1a', pillText: '#fff' },
  done: { border: '#e5dcc6', bg: '#f6f6f6', pill: '#7f7f7f', pillText: '#fff' },
};

const s = {
  wrap: { background: '#ffffff', border: '2px solid #D4AF37', borderRadius: 12, padding: '16px', marginBottom: 20, boxSizing: 'border-box', width: '100%' },
  head: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 12 },
  title: { margin: 0, fontSize: 18, fontWeight: 700, color: '#1a1a1a' },
  editBtn: { padding: '10px 14px', background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  sub: { fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#8C6D1F', fontWeight: 700, margin: '18px 0 8px' },
  dl: { display: 'flex', flexDirection: 'column', gap: 8 },
  dRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, borderRadius: 8, padding: '10px 12px', borderLeftWidth: 5, borderLeftStyle: 'solid', borderTop: '1px solid', borderRight: '1px solid', borderBottom: '1px solid' },
  dLabel: { fontWeight: 600, fontSize: 15, color: '#1a1a1a' },
  dDate: { fontSize: 13, color: '#555', marginTop: 2 },
  pill: { borderRadius: 999, padding: '4px 10px', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px 16px' },
  fLabel: { fontSize: 12, color: '#7f7f7f', marginBottom: 2 },
  fVal: { fontSize: 15, color: '#1a1a1a', wordBreak: 'break-word' },
  link: { color: '#8C6D1F', textDecoration: 'none' },
};

const Field = ({ label, children }) => (children ? (
  <div><div style={s.fLabel}>{label}</div><div style={s.fVal}>{children}</div></div>
) : null);
const Contact = ({ name, phone, email }) => (name || phone || email ? (
  <>
    {name && <div>{name}</div>}
    {phone && <div><a style={s.link} href={`tel:${String(phone).replace(/[^0-9+]/g, '')}`}>{phone}</a></div>}
    {email && <div><a style={s.link} href={`mailto:${email}`}>{email}</a></div>}
  </>
) : null);

export default function UnderContractSummary({ uc, onEdit, today }) {
  if (!uc) return null;
  const deadlines = buildUcDeadlines(uc, today);
  const mortgage = uc.financingType === 'Mortgage';
  return (
    <section style={s.wrap} aria-label="Under contract details" data-testid="uc-summary">
      <div style={s.head}>
        <h3 style={s.title}>{uc.closedDate ? 'Closed' : 'Under Contract'}</h3>
        {onEdit && <button type="button" style={s.editBtn} onClick={onEdit}>Edit in Under Contract Form</button>}
      </div>

      <div style={s.sub}>Deadlines</div>
      {deadlines.length === 0 ? <div style={s.dDate}>No deadlines entered yet.</div> : (
        <div style={s.dl}>
          {deadlines.map((d) => {
            const c = STATE_STYLE[d.state];
            return (
              <div key={d.key} style={{ ...s.dRow, background: c.bg, borderColor: c.border }} data-state={d.state}>
                <div>
                  <div style={s.dLabel}>{d.label}{d.extra ? ` · ${d.extra}` : ''}</div>
                  <div style={s.dDate}>{fmtDate(d.date)}</div>
                </div>
                <span style={{ ...s.pill, background: c.pill, color: c.pillText }}>{d.state === 'done' ? 'Closed' : daysLabel(d.days)}</span>
              </div>
            );
          })}
        </div>
      )}

      <div style={s.sub}>Contract</div>
      <div style={s.grid}>
        <Field label="Buyer names">{uc.buyerNames}</Field>
        <Field label="Under contract date">{fmtDate(uc.underContractDate)}</Field>
        <Field label="Transaction amount">{money(uc.transactionAmount)}</Field>
        <Field label="Financing type">{uc.financingType}</Field>
        <Field label="Home sale contingency">{uc.homeSaleContingency}</Field>
        <Field label="Earnest money">{join(money(uc.earnestMoneyAmount), uc.earnestMoneyDueDate && `due ${fmtDate(uc.earnestMoneyDueDate)}`)}</Field>
        <Field label="2nd EMD">{join(money(uc.secondEmdAmount), uc.secondEmdDue && `due ${fmtDate(uc.secondEmdDue)}`)}</Field>
        <Field label="Forecasted closing">{fmtDate(uc.forecastedClosedDate)}</Field>
        <Field label="Closed date">{fmtDate(uc.closedDate)}</Field>
      </div>

      <div style={s.sub}>People</div>
      <div style={s.grid}>
        <Field label="Cooperating agent"><Contact name={join(uc.cooperativeAgentFirstName, uc.cooperativeAgentLastName)} phone={uc.cooperatingAgentPhone} email={uc.cooperatingAgentEmail} /></Field>
        <Field label="Cooperating TC"><Contact name={uc.cooperatingTcName} phone={uc.cooperatingTcPhone} email={uc.cooperatingTcEmail} /></Field>
        {mortgage && <Field label="Lender"><Contact name={join(uc.lenderFirstName, uc.lenderLastName)} phone={uc.lenderPhone} email={uc.lenderEmail} /></Field>}
        <Field label="Title"><Contact name={join(uc.titleCompanyName, (uc.titleAgentFirstName || uc.titleAgentLastName) && `· ${join(uc.titleAgentFirstName, uc.titleAgentLastName)}`)} phone={uc.titleAgentPhone} email={uc.titleAgentEmail} /></Field>
        {uc.homeSaleContingency === 'Yes' && <Field label="Buyer's home listing agent"><Contact name={join(uc.homeSaleAgentFirstName, uc.homeSaleAgentLastName)} phone={uc.homeSaleAgentPhone} email={uc.homeSaleAgentEmail} /></Field>}
      </div>
      {uc.notes && (<><div style={s.sub}>Notes</div><div style={{ ...s.fVal, whiteSpace: 'pre-wrap' }}>{uc.notes}</div></>)}
    </section>
  );
}
